import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Select, { SingleValue } from "react-select";
import { Row, Col, Button, Spinner, Alert } from "reactstrap";
import { Drawer } from "antd";
import rolesApi from "../../../api/master/roles";

interface Employee {
  name: string;
  id: string;
}

interface Role {
  roleName: string;
  employees: Employee[];
  roleID: any;
  level: string;
}

interface CustomDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ApprovalData[]) => void;
  setApprovalSubmissionStatus: (status: boolean) => void;
  approvalData: ApprovalData[];
  siteId: number;
}

interface FormValues {
  [key: string]: string;
}

interface ApprovalData {
  roleId: any;
  level: string;
  userId: any;
}

const ProjectApprovalDrawer: React.FC<CustomDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  setApprovalSubmissionStatus,
  approvalData,
  siteId,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const fetchApprovalHierarchyUsersByModuleId = async (
    moduleID: number,
    siteId: number
  ) => {
    try {
      const res: any = await rolesApi.fetchHierarchyUsersByModuleId(
        moduleID,
        siteId
      );
      // makeApiCall returns the API body on success but an AxiosError-like
      // object on HTTP failure (4xx/5xx). Normalize both shapes.
      const body = res?.response?.data ?? res;
      if (body?.status === true) {
        setRoles(body.data || []);
        setErrorMsg(null);
      } else {
        setRoles([]);
        setErrorMsg(
          body?.message ||
            "Failed to load approval hierarchy. Please try again."
        );
      }
    } catch (error: any) {
      console.log(error);
      setRoles([]);
      setErrorMsg(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load approval hierarchy. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };
  const fetchModuleConfig = async () => {
    try {
      const res = await rolesApi.getModuleConfig();
      const moduleID = 82;
      console.log(moduleID);
      await fetchApprovalHierarchyUsersByModuleId(moduleID, siteId);
    } catch (err: any) {
      console.log(err);
      setErrorMsg(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load approval configuration. Please try again."
      );
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchModuleConfig();
  }, []);

  useEffect(() => {
    roles.forEach((role, index) => {
      register(role.level, { required: "This field is required" }); // uncomment this line if you want to show the required field error
    });
  }, [roles, register]);

  useEffect(() => {
    if (approvalData && approvalData.length > 0) {
      approvalData.forEach((data, index) => {
        setValue(data.level, data.userId);
      });
      setLoading(false);
    }
  }, [approvalData, setValue]);

  const submitApprovals: SubmitHandler<FormValues> = (data: any) => {
    console.log("approvalStage", data);
    const approvalData: ApprovalData[] = roles.map((role, index) => {
      const levelKey = `level${role.level}`;
      const approvalStageKey = `approvalStage_${role.level}`;

      return {
        roleId: Number(role.roleID),
        level: role.level,
        userId: data[`${role.level}`],
        approvalStage: data[approvalStageKey]?.value,
      };
    });

    onSubmit(approvalData);
    setApprovalSubmissionStatus(true);
  };

  const handleSelectChange = (
    name: string,
    value: SingleValue<{ value: string; label: string }>
  ) => {
    if (value && value.value) {
      setValue(name, value.value, { shouldValidate: true });
    }
  };

  const handleStageSelectChange = (fieldName: string, selectedOption: any) => {
    setValue(fieldName, selectedOption); // assuming you're using react-hook-form's setValue
  };

  const getDefaultValue = (role: Role) => {
    const approval = approvalData?.find((data) => data.level === role.level);
    if (approval) {
      const employee = role.employees?.find(
        (emp) => emp.id === approval.userId
      );
      if (employee) {
        return { value: employee.id, label: employee.name };
      }
    }
    return null;
  };

  return (
    <Drawer
      title="Set Project Approvals"
      placement="right"
      onClose={onClose}
      open={open}
      width={"500px"}
    >
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Spinner color="primary" />
        </div>
      ) : errorMsg ? (
        <Alert color="warning" className="mt-2">
          {errorMsg}
        </Alert>
      ) : roles.length === 0 ? (
        <Alert color="info" className="mt-2">
          No approval hierarchy is configured for this module.
        </Alert>
      ) : (
        <div>
          <p>
            There are {roles.length} levels of approvals set for this process
          </p>
          <form onSubmit={handleSubmit(submitApprovals)}>
            {roles.map((role, index) => (
              <Row className="mt-2" key={index}>
                <Col lg="6">
                  <label>{`${role.roleName}`}</label>
                  <Select
                    classNamePrefix="react-select"
                    options={role.employees?.map((employee) => ({
                      value: employee.id,
                      label: employee.name,
                    }))}
                    onChange={(selectedOption) =>
                      handleSelectChange(`${role.level}`, selectedOption)
                    }
                    placeholder={`Select ${role.roleName}`}
                    defaultValue={getDefaultValue(role)}
                  />
                  {errors[`${role.level}`] && (
                    <p className="mandatory">
                      {errors[`${role.level}`]?.message}
                    </p>
                  )}
                </Col>
                <Col lg="6">
                  <label>{`Approval Stage`}</label>
                  <Select
                    classNamePrefix="react-select"
                    options={[
                      { value: "Maker", label: "Maker" },
                      { value: "Checker", label: "Checker" },
                      { value: "Approver", label: "Approver" },
                      { value: "Verifier", label: "Verifier" },
                    ]}
                    onChange={(selectedOption) =>
                      handleStageSelectChange(
                        `approvalStage_${role.level}`,
                        selectedOption
                      )
                    }
                    placeholder={`Select Approval Stage`}
                    // defaultValue={getDefaultValue(role)}
                  />
                  {errors[`${role.level}`] && (
                    <p className="mandatory">
                      {errors[`${role.level}`]?.message}
                    </p>
                  )}
                </Col>
              </Row>
            ))}
            <Button type="submit" color="primary" className="mt-3">
              Save
            </Button>
          </form>
        </div>
      )}
    </Drawer>
  );
};

export default React.memo(ProjectApprovalDrawer);
